//source/LeagueTable/index.tsx
import React from "react";
import "./styles.css";
import logoWeb from "url:../assets/logo_web.png";
import logoMobile from "url:../assets/logo_mobile.png";
import artLeft from "url:../assets/art_left.png";
import artRight from "url:../assets/art_right.png";
import art from "url:../assets/art.png";
import type { Team } from "../types/team";
import { League } from "../constants/leagues";

interface LeagueTableProps {
  readonly league: League;
  readonly teams: Team[];
}

const getRowClassName = (key: string, rank: number) => {
  switch (rank) {
    case 2:
      if (key === "premiership") {
        return "promotion-divider";
      }
      return "";
    case 4:
      if (key != "premiership") {
        return "promotion-divider";
      }
      return "";
    case 18:
      return "relegation-divider";
    default:
      return "";
  }
};

const LeagueTable: React.FC<LeagueTableProps> = ({ league, teams }) => {
  const left = teams.slice(0, 10);
  const right = teams.slice(10, 20);

  const formatRaisedAmount = (amount: number, currencySymbol = "£") =>
    `${currencySymbol}${Math.trunc(amount).toLocaleString("en-GB")}`;

  const sideStyle = league.backgroundImage
    ? {
        backgroundImage: `url(${league.backgroundImage})`,
        backgroundSize: "cover" as const,
        backgroundPosition: "center",
      }
    : {
        backgroundColor: league.color,
      };

  return (
    <section className="league-table">
      {/* Web */}
      <div className="league-content">
        <div className="league-side league-side-left" style={sideStyle}>
          <img src={artLeft} alt="" />
          <img src={artLeft} alt="" />
        </div>

        <div className="league-main">
          <div className="league-header">
            <img src={logoWeb} alt={league.title} className="league-logo" />

            <div className="league-title">
              <div className="league-title-main">{league.title}</div>
            </div>
          </div>

          {teams.length === 0 ? (
            <div className="league-empty">
              No teams have joined this league yet. Check back soon.
            </div>
          ) : (
            <div className="league-columns">
              <table>
                <tbody>
                  {left.map((team) => (
                    <tr
                      key={team.rank}
                      className={getRowClassName(league.key, team.rank)}
                    >
                      <td className="rank">{team.rank}</td>
                      <td className="name">
                        <a
                          href={`https://justgiving.com/${team.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {team.name}
                        </a>
                      </td>
                      <td className="amount-raised">
                        {formatRaisedAmount(team.raised, team.currencySymbol)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <table>
                <tbody>
                  {right.map((team) => (
                    <tr
                      key={team.rank}
                      className={getRowClassName(league.key, team.rank)}
                    >
                      <td className="rank">{team.rank}</td>
                      <td className="name">
                        <a
                          href={`https://justgiving.com/${team.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {team.name}
                        </a>
                      </td>
                      <td className="amount-raised">
                        {formatRaisedAmount(team.raised, team.currencySymbol)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="league-side league-side-right" style={sideStyle}>
          <img src={artRight} alt="" />
          <img src={artRight} alt="" />
        </div>
      </div>

      {/* Mobile */}
      <div className="league-content-mobile" style={sideStyle}>
        <div className="league-mobile-outer">
          <div className="league-mobile-side">
            <div className="league-mobile-side-text">{league.title}</div>
            <img src={logoMobile} alt="" className="league-mobile-side-image" />
          </div>

          <div className="league-mobile-table-wrapper">
            {teams.length === 0 ? (
              <div className="league-empty">
                No teams have joined this league yet. Check back soon.
              </div>
            ) : (
              <table className="league-mobile-table">
                <tbody>
                  {teams.map((team, index) => (
                    <tr
                      key={team.rank}
                      className={getRowClassName(league.key, team.rank)}
                    >
                      <td className="rank">{index + 1}</td>
                      <td className="name">
                        <a
                          href={`https://justgiving.com/${team.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {team.name}
                        </a>
                      </td>
                      <td className="amount-raised">
                        {formatRaisedAmount(team.raised, team.currencySymbol)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <img src={art} alt="" className="league-mobile-bottom-image" />
      </div>
    </section>
  );
};

export default LeagueTable;
